import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { Transaction, Store, Product } from "../types";
import TransactionModal from "../components/TransactionModal";
import { getApiBase } from "../utils/apiBase";
import {
  Search,
  Download,
  ChevronLeft,
  ChevronRight,
  SlidersHorizontal,
  X,
  Tag,
  AlertTriangle,
  PackagePlus,
  ShieldAlert,
} from "lucide-react";

const primary = "#F1561D";

const s = {
  page: {
    padding: "32px",
    background: "#f8f7f5",
    minHeight: "100vh",
    fontFamily: "'DM Sans', sans-serif",
  } as React.CSSProperties,
  inp: {
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    padding: "9px 12px",
    fontSize: "14px",
    color: "#111827",
    outline: "none",
    background: "#fff",
    width: "100%",
    boxSizing: "border-box" as const,
  },
  sel: {
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    padding: "9px 12px",
    fontSize: "14px",
    color: "#111827",
    outline: "none",
    background: "#fff",
    width: "100%",
    cursor: "pointer",
  },
  // Keyed off the *display* status — 'flagged_for_review' is never passed in here
  // anymore, since it's rendered as a separate "Needs Review" chip instead.
  pill: (status: string) => {
    const map: Record<string, [string, string]> = {
      completed: ["#065f46", "#d1fae5"],
      pending: ["#92400e", "#fef3c7"],
      failed: ["#991b1b", "#fee2e2"],
      reversed: ["#1e40af", "#dbeafe"],
      refunded: ["#6b21a8", "#f3e8ff"],
    };
    const [color, bg] = map[status] ?? ["#374151", "#f3f4f6"];
    return {
      display: "inline-flex",
      alignItems: "center",
      padding: "3px 10px",
      borderRadius: "20px",
      fontSize: "11px",
      fontWeight: 600,
      color,
      background: bg,
    };
  },
  needsReviewPill: {
    display: "inline-flex",
    alignItems: "center",
    gap: "3px",
    padding: "3px 10px",
    borderRadius: "20px",
    fontSize: "11px",
    fontWeight: 600,
    color: "#9a3412",
    background: "#ffedd5",
  } as React.CSSProperties,
};

const STATUS_LABELS: Record<string, string> = {
  completed: "Completed",
  pending: "Pending",
  failed: "Failed",
  reversed: "Reversed",
  refunded: "Refunded",
  flagged_for_review: "Flagged for Review",
};

// A flagged transaction is a completed payment with a review flag on top of it,
// not a distinct outcome — this resolves the badge that should be shown for it.
const displayStatus = (status: string) =>
  status === "flagged_for_review" ? "completed" : status;

// Verification-log discrepancy types written by the reverse-scan flow.
// 'discrepancy'   → a purchased barcode was never scanned back (missing item)
// 'over_scanned'  → a barcode was scanned more times than it was paid for (extra item)
interface DiscrepancySummary {
  hasMissing: boolean;
  hasExtra: boolean;
}

const CustomerAvatar: React.FC<{
  name: string;
  photo: string;
  bg: string;
  textColor: string;
  ini: string;
}> = ({ name, photo, bg, textColor, ini }) => {
  const [failed, setFailed] = React.useState(false);
  return (
    <div
      style={{
        width: "36px",
        height: "36px",
        borderRadius: "50%",
        flexShrink: 0,
        overflow: "hidden",
        background: bg,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "12px",
        fontWeight: 600,
        color: textColor,
        border: "2px solid #fff",
        boxShadow: "0 0 0 1.5px #e5e7eb",
      }}
    >
      {photo && !failed ? (
        <img
          src={photo}
          alt={name}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block",
          }}
          onError={() => setFailed(true)}
        />
      ) : (
        ini
      )}
    </div>
  );
};

const Transactions: React.FC = () => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedTransaction, setSelectedTransaction] =
    useState<Transaction | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [storeFilter, setStoreFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [discrepancySummaries, setDiscrepancySummaries] = useState<
    Record<string, DiscrepancySummary>
  >({});
  const ITEMS_PER_PAGE = 20;

  const API_BASE = getApiBase();
  const token = localStorage.getItem("authToken");
  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const isManager = authUser.role === "manager";
  const managerStoreId = authUser.storeId;

  const getStoreName = (storeId: any) => {
    if (typeof storeId === "object" && storeId !== null) {
      if (storeId.name) return storeId.name;
      const s = stores.find((s) => s._id === storeId._id);
      return s ? s.name : "Unknown";
    }
    if (typeof storeId === "string") {
      const s = stores.find((s) => s._id === storeId);
      return s ? s.name : "Unknown";
    }
    return "Unknown";
  };

  const getProductName = (item: any) => {
    if (item.name) return item.name;
    if (item.isWeighed) return "Weighed Item";
    if (item.productId) {
      const p = products.find((p) => p._id === item.productId);
      if (p) return p.name;
    }
    return item.productName || "Unknown";
  };

  const normalizeImageUrl = (url?: string | null) => {
    if (!url) return "";
    return url.startsWith("http://")
      ? url.replace(/^http:\/\//i, "https://")
      : url;
  };

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: ITEMS_PER_PAGE.toString(),
      });
      if (statusFilter !== "all") params.append("status", statusFilter);
      if (isManager && managerStoreId) {
        params.append("storeId", managerStoreId);
      } else if (storeFilter !== "all") {
        params.append("storeId", storeFilter);
      }
      if (dateFilter !== "all") {
        if (dateFilter === "today") {
          const s = new Date();
          s.setHours(0, 0, 0, 0);
          const e = new Date();
          e.setHours(23, 59, 59, 999);
          params.append("fromDate", s.toISOString());
          params.append("toDate", e.toISOString());
        } else if (dateFilter === "week") {
          const s = new Date();
          s.setDate(s.getDate() - 7);
          s.setHours(0, 0, 0, 0);
          params.append("fromDate", s.toISOString());
        } else if (dateFilter === "month") {
          const s = new Date();
          s.setMonth(s.getMonth() - 1);
          s.setHours(0, 0, 0, 0);
          params.append("fromDate", s.toISOString());
        }
      }
      const res = await fetch(`${API_BASE}/api/transactions?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed");
      const json = await res.json();
      setTransactions(json.data || []);
      setTotalPages(json.meta?.pages || 1);
      setTotalCount(json.meta?.total || 0);
    } catch (err) {
      console.error(err);
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllStoresAndProducts = async () => {
    try {
      const storeRes = await fetch(`${API_BASE}/api/stores`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const storeData = await storeRes.json();
      const allStores: Store[] = storeData.data || [];
      setStores(allStores);
      const productsArray: Product[] = [];
      for (const store of allStores) {
        try {
          const r = await axios.get(
            `${API_BASE}/api/products?storeId=${store._id}`,
          );
          if (Array.isArray(r.data.data)) productsArray.push(...r.data.data);
        } catch (err) {
          console.error(err);
        }
      }
      setProducts(productsArray);
    } catch (err) {
      console.error(err);
    }
  };

  // For each flagged transaction currently on screen, pull its verification
  // logs and classify them into "missing item" vs "extra item scanned" so we
  // can badge the two discrepancy types differently instead of lumping them
  // both under a single "flagged" pill.
  const fetchDiscrepancySummaries = async (refs: string[]) => {
    try {
      const entries = await Promise.all(
        refs.map(async (ref): Promise<[string, DiscrepancySummary]> => {
          try {
            const res = await fetch(
              `${API_BASE}/api/transactions/verification-log?transactionReference=${encodeURIComponent(ref)}&limit=100`,
              { headers: { Authorization: `Bearer ${token}` } },
            );
            if (!res.ok) return [ref, { hasMissing: false, hasExtra: false }];
            const json = await res.json();
            const logs: any[] = json.data || [];
            return [
              ref,
              {
                hasMissing: logs.some((l) => l.type === "discrepancy"),
                hasExtra: logs.some((l) => l.type === "over_scanned"),
              },
            ];
          } catch {
            return [ref, { hasMissing: false, hasExtra: false }];
          }
        }),
      );
      setDiscrepancySummaries((prev) => {
        const next = { ...prev };
        entries.forEach(([ref, summary]) => {
          next[ref] = summary;
        });
        return next;
      });
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, [currentPage, statusFilter, storeFilter, dateFilter]);
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, storeFilter, dateFilter]);
  useEffect(() => {
    fetchAllStoresAndProducts();
  }, []);
  useEffect(() => {
    document.body.style.overflow = selectedTransaction ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [selectedTransaction]);

  // Fetch discrepancy summaries only for flagged transactions we haven't
  // already resolved, whenever the visible transaction list changes.
  useEffect(() => {
    const refsNeedingSummary = transactions
      .filter((tx) => tx.status === "flagged_for_review")
      .map((tx) => tx.reference)
      .filter((ref) => !(ref in discrepancySummaries));
    if (refsNeedingSummary.length > 0)
      fetchDiscrepancySummaries(refsNeedingSummary);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactions]);

  const visibleTransactions = useMemo(() => {
    if (!searchTerm) return transactions;
    return transactions.filter(
      (tx) =>
        tx.reference.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tx.customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        getStoreName(tx.storeId)
          .toLowerCase()
          .includes(searchTerm.toLowerCase()),
    );
  }, [transactions, searchTerm]);

  const exportCSV = () => {
    if (!visibleTransactions.length) {
      alert("No transactions to export!");
      return;
    }
    const headers = [
      "ID",
      "Store",
      "Customer",
      "Amount",
      "Savings",
      "Status",
      "Discrepancy",
      "Date",
      "Payment Method",
      "Items",
    ];
    const rows = visibleTransactions.map((tx) => {
      const summary = discrepancySummaries[tx.reference];
      const discrepancyLabel =
        tx.status === "flagged_for_review"
          ? [
              summary?.hasMissing ? "Missing item(s)" : null,
              summary?.hasExtra ? "Extra item(s) scanned" : null,
            ]
              .filter(Boolean)
              .join(" + ") || "Flagged"
          : "";
      return [
        tx.reference,
        getStoreName(tx.storeId),
        tx.customer.name,
        `R${tx.total.toFixed(2)}`,
        tx.totalSavings
          ? `R${(tx.totalSavings as number).toFixed(2)}`
          : "R0.00",
        // Status column always reports the real payment outcome; the
        // Discrepancy column separately notes if it needs review.
        displayStatus(tx.status),
        discrepancyLabel,
        new Date(tx.createdAt).toLocaleString(),
        tx.paymentMethod || "N/A",
        tx.items
          .map((item) => {
            const promoNames = (item as any).promotions
              ?.map((p: any) => p.name)
              .join(", ");
            return `${getProductName(item)}${item.isWeighed ? " (Weighed)" : ""} (Qty: ${item.qty}, R${item.price.toFixed(2)}${promoNames ? `, Promos: ${promoNames}` : ""})`;
          })
          .join("; "),
      ];
    });
    const csv = [headers, ...rows]
      .map((r) => r.map((c) => `"${c}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `transactions_${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const hasFilters =
    searchTerm ||
    statusFilter !== "all" ||
    storeFilter !== "all" ||
    dateFilter !== "all";
  const initials = (name: string) =>
    (name || "?")
      .split(" ")
      .map((n) => n[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();
  const avatarColors = ["#fee2e2", "#fef3c7", "#d1fae5", "#dbeafe", "#ede9fe"];
  const avatarText = ["#dc2626", "#d97706", "#059669", "#2563eb", "#7c3aed"];
  const av = (name: string) => {
    const i = (name || "").charCodeAt(0) % avatarColors.length;
    return { bg: avatarColors[i], text: avatarText[i] };
  };

  return (
    <div style={s.page}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600&display=swap');`}</style>

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: "28px",
          flexWrap: "wrap",
          gap: "12px",
        }}
      >
        <div>
          <h1
            style={{
              margin: 0,
              fontSize: "28px",
              fontWeight: 600,
              color: "#111827",
            }}
          >
            Transactions
          </h1>
          <p style={{ margin: "4px 0 0", fontSize: "14px", color: "#9ca3af" }}>
            {totalCount.toLocaleString()} transaction
            {totalCount !== 1 ? "s" : ""}
            {isManager ? " · Your store only" : ""}
          </p>
        </div>
        <button
          onClick={exportCSV}
          disabled={visibleTransactions.length === 0}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            background: visibleTransactions.length === 0 ? "#e5e7eb" : primary,
            color: "#fff",
            border: "none",
            borderRadius: "10px",
            padding: "10px 20px",
            fontSize: "14px",
            fontWeight: 500,
            cursor:
              visibleTransactions.length === 0 ? "not-allowed" : "pointer",
          }}
        >
          <Download size={16} /> Export CSV ({visibleTransactions.length})
        </button>
      </div>

      {/* ── Filters ────────────────────────────────────────────────────── */}
      <div
        style={{
          background: "#fff",
          borderRadius: "14px",
          border: "1px solid #f3f4f6",
          padding: "20px",
          marginBottom: "20px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            marginBottom: "16px",
          }}
        >
          <SlidersHorizontal size={15} color="#9ca3af" />
          <span style={{ fontSize: "13px", fontWeight: 500, color: "#374151" }}>
            Filters
          </span>
          {hasFilters && (
            <button
              onClick={() => {
                setSearchTerm("");
                setStatusFilter("all");
                setStoreFilter("all");
                setDateFilter("all");
              }}
              style={{
                marginLeft: "auto",
                display: "flex",
                alignItems: "center",
                gap: "4px",
                fontSize: "12px",
                color: primary,
                background: "none",
                border: "none",
                cursor: "pointer",
                fontWeight: 500,
              }}
            >
              <X size={12} /> Clear all
            </button>
          )}
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isManager ? "2fr 1fr 1fr" : "2fr 1fr 1fr 1fr",
            gap: "12px",
          }}
        >
          <div style={{ position: "relative" }}>
            <Search
              size={15}
              style={{
                position: "absolute",
                left: "12px",
                top: "50%",
                transform: "translateY(-50%)",
                color: "#9ca3af",
              }}
            />
            <input
              type="text"
              placeholder="Search by ID, customer, store…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ ...s.inp, paddingLeft: "36px" }}
            />
          </div>
          {!isManager && (
            <select
              style={s.sel}
              value={storeFilter}
              onChange={(e) => setStoreFilter(e.target.value)}
            >
              <option value="all">All Stores</option>
              {stores.map((store) => (
                <option key={store._id} value={store._id}>
                  {store.name}
                </option>
              ))}
            </select>
          )}
          <select
            style={s.sel}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Statuses</option>
            {Object.entries(STATUS_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
          <select
            style={s.sel}
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
          >
            <option value="all">All Time</option>
            <option value="today">Today</option>
            <option value="week">Last 7 Days</option>
            <option value="month">Last 30 Days</option>
          </select>
        </div>
      </div>

      {/* ── Table ──────────────────────────────────────────────────────── */}
      <div
        style={{
          background: "#fff",
          borderRadius: "14px",
          border: "1px solid #f3f4f6",
          overflow: "hidden",
        }}
      >
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr
              style={{
                borderBottom: "1px solid #f3f4f6",
                background: "#fafafa",
              }}
            >
              {[
                "Reference",
                "Store",
                "Customer",
                "Amount",
                "Items",
                "Status",
                "Date",
                "",
              ].map((h) => (
                <th
                  key={h}
                  style={{
                    padding: "12px 20px",
                    textAlign: "left",
                    fontSize: "11px",
                    color: "#9ca3af",
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan={8}
                  style={{
                    padding: "48px",
                    textAlign: "center",
                    color: "#9ca3af",
                    fontSize: "14px",
                  }}
                >
                  Loading transactions…
                </td>
              </tr>
            ) : visibleTransactions.length === 0 ? (
              <tr>
                <td
                  colSpan={8}
                  style={{
                    padding: "48px",
                    textAlign: "center",
                    color: "#9ca3af",
                    fontSize: "14px",
                  }}
                >
                  No transactions found
                </td>
              </tr>
            ) : (
              visibleTransactions.map((tx, idx) => {
                const { bg, text } = av(tx.customer.name);
                const photo = normalizeImageUrl((tx.customer as any)?.photo);
                const totalSavings = (tx as any).totalSavings as
                  | number
                  | undefined;
                const hasPromos = tx.items.some(
                  (i) => (i as any).promotions?.length > 0,
                );
                const discrepancy = discrepancySummaries[tx.reference];
                const isFlagged = tx.status === "flagged_for_review";

                return (
                  <tr
                    key={`${tx._id}-${idx}`}
                    style={{ borderBottom: "1px solid #f9fafb" }}
                    onMouseEnter={(e) =>
                      ((
                        e.currentTarget as HTMLTableRowElement
                      ).style.background = "#fafafa")
                    }
                    onMouseLeave={(e) =>
                      ((
                        e.currentTarget as HTMLTableRowElement
                      ).style.background = "transparent")
                    }
                  >
                    <td
                      style={{
                        padding: "14px 20px",
                        fontSize: "12px",
                        color: "#9ca3af",
                        fontFamily: "monospace",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {tx.reference}
                    </td>
                    <td
                      style={{
                        padding: "14px 20px",
                        fontSize: "13px",
                        color: "#6b7280",
                      }}
                    >
                      {getStoreName(tx.storeId)}
                    </td>
                    <td style={{ padding: "14px 20px" }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                        }}
                      >
                        <CustomerAvatar
                          name={tx.customer.name}
                          photo={photo}
                          bg={bg}
                          textColor={text}
                          ini={initials(tx.customer.name)}
                        />
                        <span
                          style={{
                            fontSize: "14px",
                            fontWeight: 500,
                            color: "#111827",
                          }}
                        >
                          {tx.customer.name}
                        </span>
                      </div>
                    </td>

                    {/* ── Amount + savings ── */}
                    <td style={{ padding: "14px 20px", whiteSpace: "nowrap" }}>
                      <p
                        style={{
                          margin: 0,
                          fontSize: "14px",
                          fontWeight: 600,
                          color: "#111827",
                        }}
                      >
                        R {tx.total.toFixed(2)}
                      </p>
                      {totalSavings && totalSavings > 0 ? (
                        <p
                          style={{
                            margin: "2px 0 0",
                            fontSize: "11px",
                            color: "#16a34a",
                            fontWeight: 500,
                          }}
                        >
                          Saved R {totalSavings.toFixed(2)}
                        </p>
                      ) : null}
                    </td>

                    {/* ── Items + promo badge ── */}
                    <td style={{ padding: "14px 20px" }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          fontSize: "13px",
                          color: "#6b7280",
                          flexWrap: "wrap",
                        }}
                      >
                        {tx.items.length} item{tx.items.length !== 1 ? "s" : ""}
                        {tx.items.some((i) => i.isWeighed) && (
                          <span
                            style={{
                              background: "#fff5f2",
                              color: "#ea580c",
                              fontSize: "10px",
                              fontWeight: 600,
                              padding: "2px 7px",
                              borderRadius: "20px",
                            }}
                          >
                            Weighed
                          </span>
                        )}
                        {hasPromos && (
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "3px",
                              background: "#f0fdf4",
                              color: "#16a34a",
                              fontSize: "10px",
                              fontWeight: 600,
                              padding: "2px 7px",
                              borderRadius: "20px",
                              border: "1px solid #bbf7d0",
                            }}
                          >
                            <Tag size={9} /> Promo
                          </span>
                        )}
                      </div>
                    </td>

                    {/* ── Status + Needs Review + discrepancy type badges ── */}
                    <td style={{ padding: "14px 20px" }}>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "5px",
                          alignItems: "flex-start",
                        }}
                      >
                        {/* Status pill always reads the real payment outcome */}
                        <div style={{ display: "flex", gap: "5px", flexWrap: "wrap" }}>
                          <span style={s.pill(displayStatus(tx.status))}>
                            {STATUS_LABELS[displayStatus(tx.status)] ??
                              displayStatus(tx.status)}
                          </span>
                          {/* Needs Review side-note — shown alongside, not instead of, the status */}
                          {isFlagged && (
                            <span style={s.needsReviewPill}>
                              <ShieldAlert size={9} /> Needs Review
                            </span>
                          )}
                        </div>
                        {isFlagged && (
                          <div
                            style={{
                              display: "flex",
                              gap: "4px",
                              flexWrap: "wrap",
                            }}
                          >
                            {!discrepancy ? (
                              <span
                                style={{ fontSize: "10px", color: "#9ca3af" }}
                              >
                                Checking…
                              </span>
                            ) : (
                              <>
                                {discrepancy.hasMissing && (
                                  <span
                                    style={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: "3px",
                                      background: "#fef2f2",
                                      color: "#dc2626",
                                      fontSize: "10px",
                                      fontWeight: 600,
                                      padding: "2px 7px",
                                      borderRadius: "20px",
                                      border: "1px solid #fecaca",
                                    }}
                                  >
                                    <AlertTriangle size={9} /> Missing item(s)
                                  </span>
                                )}
                                {discrepancy.hasExtra && (
                                  <span
                                    style={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: "3px",
                                      background: "#fff7ed",
                                      color: "#c2410c",
                                      fontSize: "10px",
                                      fontWeight: 600,
                                      padding: "2px 7px",
                                      borderRadius: "20px",
                                      border: "1px solid #fed7aa",
                                    }}
                                  >
                                    <PackagePlus size={9} /> Extra item(s)
                                    scanned
                                  </span>
                                )}
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: "14px 20px", whiteSpace: "nowrap" }}>
                      <p
                        style={{
                          margin: 0,
                          fontSize: "13px",
                          color: "#111827",
                        }}
                      >
                        {new Date(tx.createdAt).toLocaleDateString()}
                      </p>
                      <p
                        style={{
                          margin: "2px 0 0",
                          fontSize: "11px",
                          color: "#9ca3af",
                        }}
                      >
                        {new Date(tx.createdAt).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </p>
                    </td>
                    <td style={{ padding: "14px 20px" }}>
                      <button
                        onClick={() => setSelectedTransaction(tx)}
                        style={{
                          fontSize: "13px",
                          fontWeight: 500,
                          color: primary,
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          whiteSpace: "nowrap",
                        }}
                      >
                        View →
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>

        {/* ── Pagination ───────────────────────────────────────────────── */}
        {totalPages > 1 && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "16px 20px",
              borderTop: "1px solid #f3f4f6",
            }}
          >
            <span style={{ fontSize: "13px", color: "#9ca3af" }}>
              Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1}–
              {Math.min(currentPage * ITEMS_PER_PAGE, totalCount)} of{" "}
              {totalCount}
            </span>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  padding: "7px 14px",
                  borderRadius: "8px",
                  border: "1px solid #e5e7eb",
                  background: "#fff",
                  fontSize: "13px",
                  fontWeight: 500,
                  color: currentPage === 1 ? "#d1d5db" : "#374151",
                  cursor: currentPage === 1 ? "not-allowed" : "pointer",
                }}
              >
                <ChevronLeft size={14} /> Prev
              </button>
              <span
                style={{ fontSize: "13px", color: "#374151", padding: "0 8px" }}
              >
                Page {currentPage} of {totalPages}
              </span>
              <button
                disabled={currentPage === totalPages}
                onClick={() =>
                  setCurrentPage((p) => Math.min(p + 1, totalPages))
                }
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  padding: "7px 14px",
                  borderRadius: "8px",
                  border: "1px solid #e5e7eb",
                  background: "#fff",
                  fontSize: "13px",
                  fontWeight: 500,
                  color: currentPage === totalPages ? "#d1d5db" : "#374151",
                  cursor:
                    currentPage === totalPages ? "not-allowed" : "pointer",
                }}
              >
                Next <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>

      {selectedTransaction && (
        <TransactionModal
          transaction={selectedTransaction}
          stores={stores}
          products={products}
          onClose={() => setSelectedTransaction(null)}
        />
      )}
    </div>
  );
};

export default Transactions;
